import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import PitBoardLogo from "@/components/PitBoardLogo";

/**
 * Modal bloquant l'application tant que l'utilisateur n'a pas accepté la version
 * courante des CGV. Affiché aussi bien à la 1ère connexion qu'aux mises à jour
 * substantielles (LEGAL_VERSION bumpée côté backend).
 *
 * Reçoit : `currentVersion` (string, ex: "v1.2-2026-06").
 * À l'acceptation : POST /api/legal/accept puis refresh du contexte auth.
 */
export default function CGVAcceptanceModal({ currentVersion, acceptedVersion }) {
  const { refresh, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isUpdate = Boolean(acceptedVersion);

  const submit = async () => {
    if (!accepted) {
      setError("Vous devez cocher la case pour accepter les CGV.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/legal/accept");
      await refresh();
      // Si refresh ne déclenche pas un démontage immédiat, on garde l'écran « accepté »
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur lors de l'enregistrement. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="cgv-acceptance-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg rounded-xl border shadow-2xl"
        style={{ background: "var(--carbon-2)", borderColor: "var(--steel)" }}
      >
        {/* Bandeau jaune signature */}
        <div className="h-1.5 rounded-t-xl" style={{ background: "var(--pit-yellow)" }} />

        <div className="px-7 py-6">
          <div className="flex items-center gap-3 mb-4">
            <PitBoardLogo size={36} />
            <span className="font-display text-xs font-bold tracking-widest" style={{ color: "var(--pit-yellow)" }}>
              {isUpdate ? "MISE À JOUR LÉGALE" : "BIENVENUE SUR PITBOARD"}
            </span>
          </div>

          <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-3 leading-[1.1]" style={{ color: "var(--chalk)" }}>
            {isUpdate ? (
              <>Nos CGV ont été mises à jour.</>
            ) : (
              <>Avant de commencer, acceptez nos CGV.</>
            )}
          </h2>

          <p className="text-[14px] leading-relaxed mb-5" style={{ color: "var(--ink-soft)" }}>
            {isUpdate ? (
              <>
                Nous avons mis à jour nos <b>Conditions Générales de Vente</b> et nos{" "}
                <b>Mentions Légales</b>. Pour continuer à utiliser PitBoard, merci de prendre
                connaissance de la nouvelle version et de l&apos;accepter.
              </>
            ) : (
              <>
                Pour utiliser PitBoard, vous devez prendre connaissance et accepter nos{" "}
                <b>Conditions Générales de Vente</b> et nos <b>Mentions Légales</b>. Elles
                définissent le cadre contractuel de votre utilisation du service.
              </>
            )}
          </p>

          {/* Encadré "à lire" */}
          <div
            className="rounded-lg p-4 mb-5 border flex items-start gap-3"
            style={{ background: "rgba(255,196,0,0.06)", borderColor: "rgba(255,196,0,0.30)" }}
          >
            <FileText size={18} style={{ color: "var(--pit-yellow)" }} className="shrink-0 mt-0.5" />
            <div className="text-[13px] leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              <Link
                to="/cgv"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold"
                style={{ color: "var(--pit-yellow)" }}
                data-testid="cgv-read-link"
              >
                Lire les CGV et Mentions Légales (nouvel onglet) ↗
              </Link>
              <div className="text-xs mt-1.5" style={{ color: "var(--grey)" }}>
                Version <span className="font-mono">{currentVersion}</span>
                {isUpdate && acceptedVersion && (
                  <> · Précédente acceptée : <span className="font-mono">{acceptedVersion}</span></>
                )}
              </div>
            </div>
          </div>

          {/* Checkbox */}
          <label
            htmlFor="cgv-checkbox"
            className="flex items-start gap-3 mb-5 cursor-pointer select-none p-3 rounded-lg border transition-colors"
            style={{
              background: accepted ? "rgba(16,185,129,0.08)" : "var(--carbon)",
              borderColor: accepted ? "rgba(16,185,129,0.45)" : "var(--steel)",
            }}
          >
            <input
              id="cgv-checkbox"
              type="checkbox"
              checked={accepted}
              onChange={(e) => { setAccepted(e.target.checked); setError(null); }}
              className="mt-0.5 w-4 h-4 cursor-pointer accent-[var(--pit-yellow)]"
              data-testid="cgv-accept-checkbox"
            />
            <span className="text-[13px] leading-relaxed" style={{ color: "var(--chalk)" }}>
              J&apos;ai lu et j&apos;accepte les{" "}
              <Link to="/cgv" target="_blank" className="underline" style={{ color: "var(--pit-yellow)" }}>
                Conditions Générales de Vente
              </Link>
              {" "}et les{" "}
              <Link to="/mentions-legales" target="_blank" className="underline" style={{ color: "var(--pit-yellow)" }}>
                Mentions Légales
              </Link>
              {" "}de PitBoard (version {currentVersion}).
            </span>
          </label>

          {error && (
            <div
              className="rounded-lg p-3 mb-4 flex items-start gap-2.5"
              style={{ background: "rgba(225,6,0,0.10)", border: "1px solid rgba(225,6,0,0.35)" }}
              data-testid="cgv-error"
            >
              <AlertCircle size={16} style={{ color: "#E10600" }} className="shrink-0 mt-0.5" />
              <div className="text-[13px]" style={{ color: "var(--chalk)" }}>{error}</div>
            </div>
          )}

          <button
            onClick={submit}
            disabled={submitting || !accepted}
            className="btn btn-primary w-full"
            style={{
              minHeight: 50,
              fontFamily: "'Inter', sans-serif",
              letterSpacing: 0,
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 600,
              opacity: submitting || !accepted ? 0.5 : 1,
            }}
            data-testid="cgv-accept-submit"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /> Enregistrement…</>
            ) : (
              <><CheckCircle2 size={16} /> Accepter et continuer</>
            )}
          </button>

          <p className="text-[11px] mt-4 text-center" style={{ color: "var(--grey)" }}>
            En cas de refus, vous pouvez{" "}
            <button
              type="button"
              onClick={logout}
              className="underline hover:text-[var(--chalk)]"
              data-testid="cgv-decline-logout"
            >
              vous déconnecter
            </button>.
            {" "}Nous conservons votre acceptation et sa date à des fins de preuve.
          </p>
        </div>
      </div>
    </div>
  );
}
